import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useLessonStudents from "../../../hooks/useLessonStudentData";
import useLessons from "../../../hooks/useLessons";
import DropdownUniqueUsersInDisplayData from "../../../unicomponents/Dropdowns/DropdownUniqueUsersInDisplayData";
import DropdownTermDate from "../../../unicomponents/Dropdowns/DropdownTermDate";
import DropdownHierarchyThree from "../../../unicomponents/Dropdowns/DropdownHierarchyThree";
import DropdownHierarchyTwo from "../../../unicomponents/Dropdowns/DropdownHierarchyTwo";
import DropdownHierarchyOne from "../../../unicomponents/Dropdowns/DropdownHierarchyOne";
import DropdownSchoolYear from "../../../unicomponents/Dropdowns/DropdownSchoolYear";
import useOrganizationSetting from "../../../hooks/useOrganizationSetting";
import useTermPeriod from "../../../hooks/useTermPeriod";
import { setTermIdByToday } from "../../../util/dateRelated";
import { useAuthContext } from "../../../context/AuthContext";
import useUsers from "../../../hooks/useUsers";
import useLessonSetting from "../../../hooks/useLessonSetting";
import DropdownUniqueItems from "../../../unicomponents/Dropdowns/DropdownUniqueItems";
import SimpleAlertMessageBlock from "../../../unicomponents/SimpleAlertMessageBlock";
import IntelGrowthStudentsTable from "./IntelGrowthStudentsTable";
import { getMadangPairLabels } from "./intelGrowthTermUtils";

const SAEUM_SCHOOL_ID = "saeumschool";

/** Same rules as DoLessons lesson list filter */
function filterDataByOrganSettingDropdown(
  arrayDataToBeFiltered,
  organSettingObj,
  termIdVal,
  selectedUser,
) {
  if (!arrayDataToBeFiltered) return [];

  return arrayDataToBeFiltered.filter((record) => {
    const matchesSchoolYearId = organSettingObj.schoolYearId
      ? String(record.school_year_id) === String(organSettingObj.schoolYearId)
      : true;
    const matchesHierarchyOne = organSettingObj.hierarchyOneId
      ? record.hierarchy_one === organSettingObj.hierarchyOneId
      : true;
    const matchesHierarchyTwo = organSettingObj.hierarchyTwoId
      ? record.hierarchy_two === organSettingObj.hierarchyTwoId
      : true;
    const matchesHierarchyThree = organSettingObj.hierarchyThreeId
      ? record.hierarchy_three === organSettingObj.hierarchyThreeId
      : true;
    const matchesTermId = termIdVal
      ? String(record.term_id) === String(termIdVal)
      : true;
    const matchesSelectedUser = selectedUser
      ? record.teachers.some((teacher) => teacher.username === selectedUser)
      : true;

    return (
      matchesSchoolYearId &&
      matchesHierarchyOne &&
      matchesHierarchyTwo &&
      matchesHierarchyThree &&
      matchesTermId &&
      matchesSelectedUser
    );
  });
}

export default function SaeumschoolLessonExtraData() {
  const { t } = useTranslation();
  const location = useLocation();
  const paths = location.pathname.split("/");
  const schoolId = paths[1];
  const conditions = { schoolid: schoolId };

  const {
    settingQuery: { data: settingData },
  } = useOrganizationSetting(conditions);

  const {
    termPeriodQuery: { data: termPeriodData },
  } = useTermPeriod(conditions);

  const {
    lessonsQuery: { data: lessonsData },
  } = useLessons(conditions);

  const {
    lessonSettingQuery: { data: lessonSettingData },
  } = useLessonSetting(conditions);

  const {
    usersQuery: { data: userData },
  } = useUsers(conditions, { role: "teacher", status: 1 });

  const {
    getLessonStudentsByLessonId,
    updateLessonStudents,
    updateLessonStudentsBulk,
  } = useLessonStudents(conditions);

  const { user, hasPermission } = useAuthContext();

  const [filters, setFilters] = useState({
    schoolYear: null,
    schoolYearId: "",
    hierarchyOneId: "",
    hierarchyTwoId: "",
    hierarchyThreeId: "",
    organPathForSort: "/",
    termId: "",
    selectedTeacherId: "",
    selectedLessonId: "",
  });

  const [termId, setTermId] = useState("");
  const [lessonList, setLessonList] = useState([]);
  const [lessonInfo, setLessonInfo] = useState({});
  const [lessonSetting, setLessonSetting] = useState([]);
  const [lessonStudentsData, setLessonStudentsData] = useState([]);

  useEffect(() => {
    if (
      filters.schoolYear === null &&
      lessonsData &&
      settingData &&
      settingData.length > 0
    ) {
      const initialData = settingData.sort(
        (a, b) => b.school_year - a.school_year,
      )[0];
      setFilters((prev) => ({
        ...prev,
        schoolYear: initialData.school_year,
        schoolYearId: initialData.id,
      }));

      if (termPeriodData) {
        setTermIdByToday(termPeriodData, setTermId, initialData.id);
      }
    }
  }, [lessonsData, filters.schoolYear, settingData, termPeriodData]);

  useEffect(() => {
    if (lessonsData) {
      const sortedLessons = filterDataByOrganSettingDropdown(
        lessonsData,
        filters,
        termId,
        filters.selectedTeacherId,
      );
      setLessonList(sortedLessons);
    }
  }, [lessonsData, filters, termId]);

  useEffect(() => {
    if (userData && user?.role === "teacher") {
      setFilters((prev) => ({
        ...prev,
        selectedTeacherId: user.username,
      }));
    }
  }, [user, userData]);

  useEffect(() => {
    if (lessonSettingData && termPeriodData) {
      let thisTermEndDate;

      if (termId) {
        const termData = termPeriodData.find((period) => period.id === termId);
        thisTermEndDate = termData ? new Date(termData.end_date) : undefined;
      } else {
        thisTermEndDate = new Date(
          Math.max(
            ...termPeriodData.map((period) =>
              new Date(period.end_date).getTime(),
            ),
          ),
        );
      }

      if (thisTermEndDate) {
        const filteredAndSortedLessonSetting = lessonSettingData
          .filter((r) => new Date(r.start_date) <= thisTermEndDate)
          .sort((a, b) => new Date(b.start_date) - new Date(a.start_date));

        const uniqueLessonSetting = filteredAndSortedLessonSetting.reduce(
          (acc, current) => {
            const x = acc.find((item) => item.item_code === current.item_code);
            if (!x) {
              return acc.concat([current]);
            }
            return acc;
          },
          [],
        );
        setLessonSetting(uniqueLessonSetting);
      }
    }
  }, [lessonSettingData, termId, termPeriodData]);

  useEffect(() => {
    if (!filters.selectedLessonId) {
      setLessonInfo({});
      return;
    }
    const selected = lessonList.find(
      (lesson) => lesson.id.toString() === filters.selectedLessonId.toString(),
    );
    setLessonInfo(selected || {});
  }, [filters.selectedLessonId, lessonList]);

  function queryLessonStudentsData(forWhere) {
    getLessonStudentsByLessonId.mutate(forWhere, {
      onSuccess: (data) => {
        if (!settingData?.length) {
          setLessonStudentsData(
            data.map((row) => ({ ...row, isModified: false })),
          );
          return;
        }
        const settingMap = settingData.reduce((map, setting) => {
          map[setting.id] = setting.group_name;
          return map;
        }, {});

        const updatedData = data.map((studentData) => {
          const pathParts = (studentData.organization_path || "").split("/");
          const stageId = pathParts[2];
          const yearId = pathParts[3];
          const classId = pathParts[4];

          return {
            ...studentData,
            hierarchyOne: settingMap[stageId],
            hierarchyTwo: settingMap[yearId],
            hierarchyThree: settingMap[classId],
            isModified: false,
          };
        });
        setLessonStudentsData(updatedData);
      },
      onError: (error) => {
        console.error(error);
        alert(t("common.messages.errorAlert") + error.message);
      },
    });
  }

  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    const selectedOptionText = e.target.selectedOptions[0]?.text;

    if (name === "0") {
      setFilters((prev) => ({
        ...prev,
        schoolYear: selectedOptionText,
        schoolYearId: value,
        hierarchyOneId: "",
      }));
      setTermIdByToday(termPeriodData, setTermId, value);
    }

    if (name === "1") {
      setFilters((prev) => ({
        ...prev,
        hierarchyOneId: value,
        hierarchyTwoId: "",
        hierarchyThreeId: "",
      }));
      setTermIdByToday(termPeriodData, setTermId, filters.schoolYearId, value);
    }

    if (name === "2") {
      setFilters((prev) => ({
        ...prev,
        hierarchyTwoId: value,
        hierarchyThreeId: "",
      }));
      setTermIdByToday(
        termPeriodData,
        setTermId,
        filters.schoolYearId,
        filters.hierarchyOneId,
        value,
      );
    }

    if (name === "3") {
      setFilters((prev) => ({
        ...prev,
        hierarchyThreeId: value,
      }));
    }

    if (name === "termDate") {
      setTermId(value);
    }

    if (name === "teachers") {
      setFilters((prev) => ({
        ...prev,
        selectedTeacherId: value,
      }));
    }

    if (name === "lessons") {
      setFilters((prev) => ({
        ...prev,
        selectedLessonId: value,
      }));

      if (value) {
        queryLessonStudentsData({
          schoolid: schoolId,
          lesson_id: value,
        });
      } else {
        setLessonStudentsData([]);
      }
    }
  };

  const madangLabels = useMemo(
    () =>
      getMadangPairLabels(termPeriodData, {
        schoolYearId: filters.schoolYearId,
        hierarchyOneId: filters.hierarchyOneId,
        hierarchyTwoId: filters.hierarchyTwoId,
        termId,
      }),
    [
      termPeriodData,
      filters.schoolYearId,
      filters.hierarchyOneId,
      filters.hierarchyTwoId,
      termId,
    ],
  );

  const hasUpdatePermission = () => {
    const isOwner = lessonInfo?.teachers?.some(
      (teacher) => teacher.username === user?.username,
    );
    return isOwner || hasPermission(null, "can_update");
  };

  const canAccessLesson =
    lessonInfo?.teachers?.some(
      (teacher) => teacher.username === user?.username,
    ) || hasPermission(null, "can_read");

  if (schoolId !== SAEUM_SCHOOL_ID && schoolId !== "sampleschool") {
    return (
      <div className="basicWidth">
        <div className="container-fluid mt-3">
          <SimpleAlertMessageBlock
            message={
              t("teacher.saeumschool.intelGrowth.wrongSchool") ||
              "이 페이지는 새음학교 전용입니다."
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="basicWidth">
      <div className="container-fluid">
        <div className="d-flex justify-content-end flex-wrap mb-3 mt-2">
          <div style={{ width: "95px" }} className="me-1">
            <DropdownSchoolYear
              settingData={settingData}
              handleDropdownChange={handleDropdownChange}
              schoolYearId={filters.schoolYearId}
              allYears={false}
            />
          </div>
          <div style={{ width: "95px" }} className="me-1">
            <DropdownHierarchyOne
              settingData={settingData}
              handleDropdownChange={handleDropdownChange}
              schoolYear={filters.schoolYear}
              hierarchyOneId={filters.hierarchyOneId}
              hierarchyOneTitle={t("common.labels.whole")}
              extraOption={true}
              extraOptionTitle={t("common.labels.schoolStage")}
            />
          </div>
          <div style={{ width: "95px" }} className="me-1">
            <DropdownHierarchyTwo
              settingData={settingData}
              handleDropdownChange={handleDropdownChange}
              schoolYear={filters.schoolYear}
              hierarchyOneId={filters.hierarchyOneId}
              hierarchyTwoId={filters.hierarchyTwoId}
              hierarchyTwoTitle={t("common.labels.whole")}
              extraOption={true}
              extraOptionTitle={t("common.labels.studentYear")}
            />
          </div>
          <div style={{ width: "95px" }} className="me-1">
            <DropdownHierarchyThree
              settingData={settingData}
              handleDropdownChange={handleDropdownChange}
              schoolYear={filters.schoolYear}
              hierarchyTwoId={filters.hierarchyTwoId}
              hierarchyThreeId={filters.hierarchyThreeId}
              hierarchyThreeTitle={t("common.labels.whole")}
              extraOption={true}
              extraOptionTitle={t("common.labels.class")}
            />
          </div>
          <div style={{ width: "95px" }} className="me-1">
            <DropdownTermDate
              termPeriodData={termPeriodData}
              handleDropdownChange={handleDropdownChange}
              termId={termId}
              schoolYearId={filters.schoolYearId}
              hierarchyOneId={filters.hierarchyOneId}
              hierarchyTwoId={filters.hierarchyTwoId}
            />
          </div>
          <div style={{ width: "95px" }}>
            <DropdownUniqueUsersInDisplayData
              displayData={userData}
              displayKey={"fullname"}
              valueKey={"username"}
              uniqueKey={"username"}
              sortKey={"fullname"}
              isDesc={false}
              handleDropdownChange={handleDropdownChange}
              selectedUser={filters.selectedTeacherId}
              selectName="teachers"
              dropdownTitle={t("teacher.lessonRelated.selectTeacher")}
            />
          </div>

          <div style={{ width: "95px" }}>
            <DropdownUniqueItems
              displayData={lessonList}
              displayKey={"subject_name"}
              valueKey={"id"}
              uniqueKey={"id"}
              sortKey={"subject_name"}
              isDesc={false}
              handleDropdownChange={handleDropdownChange}
              selectedItem={filters.selectedLessonId}
              selectName="lessons"
              dropdownTitle={t("teacher.lessonRelated.selectSubject")}
            />
          </div>
        </div>

        <div className="row align-items-center">
          <div className="col-auto">
            <h3 className="fw-bold">
              {t("teacher.saeumschool.intelGrowth.pageTitle") ||
                "지적 성장 (마당) 입력"}
            </h3>
          </div>
        </div>
        {!filters.selectedLessonId ? (
          <SimpleAlertMessageBlock
            message={t("teacher.lessonRelated.selectSubjectMessage")}
          />
        ) : !canAccessLesson ? (
          <SimpleAlertMessageBlock
            message={t("common.messages.noAccessPermission")}
          />
        ) : (
          <IntelGrowthStudentsTable
            lessonStudentsData={lessonStudentsData}
            setLessonStudentsData={setLessonStudentsData}
            lessonInfo={lessonInfo}
            lessonSetting={lessonSetting}
            updateLessonStudents={updateLessonStudents}
            updateLessonStudentsBulk={updateLessonStudentsBulk}
            schoolId={schoolId}
            madangLabels={madangLabels}
            hasUpdatePermission={hasUpdatePermission}
          />
        )}
      </div>
    </div>
  );
}
